<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { botPersonasAPI } from 'dashboard/api/botPersonas';
import BotPersonaEditor from './BotPersonaEditor.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();

const personaId = computed(() => route.params.personaId);
const isNew = computed(() => !personaId.value || personaId.value === 'new');

const personas = computed(() => store.getters['botPersonas/getPersonas']);
const persona = computed(() =>
  isNew.value
    ? null
    : personas.value.find(p => String(p.id) === String(personaId.value))
);

const pageTitle = computed(() => {
  if (isNew.value) return t('MINISTRY.BOT_PERSONAS.NEW_PERSONA');
  return `${t('MINISTRY.BOT_PERSONAS.EDIT_PERSONA')} — ${persona.value?.name ?? ''}`;
});

onMounted(() => {
  if (personas.value.length === 0) store.dispatch('botPersonas/fetch');
});

function goBack() {
  router.push({
    name: 'bot_personas',
    params: { accountId: route.params.accountId },
  });
}

function onSaved() {
  useAlert(t('MINISTRY.BOT_PERSONAS.SAVE_SUCCESS'));
  goBack();
}

const testingInbox = ref(false);

async function openTestInbox() {
  if (!personaId.value) return;
  testingInbox.value = true;
  try {
    const { data } = await botPersonasAPI.testInbox(personaId.value);
    window.open(data.test_url, '_blank');
  } finally {
    testingInbox.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col h-full bg-[#F5F3EE]">
    <!-- Header -->
    <div
      class="bg-white border-b border-[#E8E6E0] px-10 py-4 flex items-center gap-4"
    >
      <button
        class="p-1.5 rounded-lg hover:bg-[#F5F3EE] text-[#6B7280] transition-colors"
        @click="goBack"
      >
        <span class="i-lucide-arrow-left size-5" />
      </button>
      <h1 class="text-[22px] font-bold text-[#1A1A2E] flex-1">
        {{ pageTitle }}
      </h1>
      <button
        v-if="!isNew"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E8E6E0] text-[13px] font-medium text-[#6B7280] hover:border-[#C9A84C] hover:text-[#C9A84C] transition-colors disabled:opacity-50"
        :disabled="testingInbox"
        @click="openTestInbox"
      >
        <span
          :class="
            testingInbox
              ? 'i-lucide-loader-circle animate-spin size-4'
              : 'i-lucide-flask-conical size-4'
          "
        />
        {{ t('MINISTRY.BOT_PERSONAS.TEST_BOT') }}
      </button>
    </div>

    <!-- Editor body -->
    <div class="flex-1 overflow-hidden">
      <BotPersonaEditor :persona="persona" @saved="onSaved" @close="goBack" />
    </div>
  </div>
</template>
